// utils/destructiveSemester.ts
/**
 * Helpers for the RC5-1 destructive-semester-change safety guard.
 *
 * The backend's PUT /api/academic-plans/:id/semesters rejects any change that
 * would delete a semester still holding courses with HTTP 409 + error_code
 * 'destructive_semester_change', carrying the list of semesters that would be
 * deleted. Callers surface that list and retry with confirm_destructive=true
 * only after the user confirms.
 */
import { ApiError } from './apiClient';

/** One entry in a 409 destructive_semester_change response's would-delete list. */
export interface SemesterToDelete {
  semester_code: string;
  semester_name: string;
  display_order: number;
  course_count: number;
}

/**
 * Return the would-delete list if `err` is a destructive_semester_change 409,
 * otherwise null (so the caller can rethrow / fall through to generic handling).
 */
export function extractSemestersToDelete(err: unknown): SemesterToDelete[] | null {
  if (!(err instanceof ApiError) || err.status !== 409 ||
      err.code !== 'destructive_semester_change') {
    return null;
  }
  const details = (err.details as { details?: { semesters_to_delete?: SemesterToDelete[] } })?.details;
  return details?.semesters_to_delete ?? [];
}

/** Human-readable "Fall 2026 (2 courses), Spring 2027 (3 courses)" summary. */
export function summarizeSemestersToDelete(list: SemesterToDelete[]): string {
  return list
    .map(s => `${s.semester_name} (${s.course_count} course${s.course_count !== 1 ? 's' : ''})`)
    .join(', ');
}
